import ELK, { ElkExtendedEdge } from 'elkjs/lib/elk.bundled.js';
import type {
  CalculationSourceItem,
  DataObjectRelationsDataConflictsItem,
} from '@atlas-frontend/sdk';
import { getGroupDescriptionRef } from '@atlas-frontend/sdk';
import { path } from '../../paths';
import { withParams } from '../../paths/withParams';
import { NodeData, RelationEdgeType, RelationNodeType } from './RelationNode';
import { MarkerType, Position } from '@xyflow/react';
import {
  ActivePeriod,
  DataObjectRelationEdgeType,
  DataObjectType,
  DataObjectVersionType,
  getLatestDataObjectVersion,
} from '@atlas-frontend/sdk';

const elk = new ELK();
const elkOptions = {
  'elk.algorithm': 'layered',
  'elk.layered.spacing.nodeNodeBetweenLayers': '100',
  'elk.spacing.nodeNode': '80',
  'elk.direction': 'RIGHT',
};

export const InputNodeAction = {
  OpenInputData: 'openInputData',
  OpenInputDescription: 'openInputDescription',
  OpenRelationsGraph: 'openRelationsGraph',
  OpenRelationsTable: 'openRelationsTable',
  OpenRelatedInputs: 'openRelatedInputs',
} as const;

export type InputNodeAction =
  (typeof InputNodeAction)[keyof typeof InputNodeAction];

export const OutputNodeAction = {
  OpenOutputData: 'openOutputData',
  OpenOutputDescription: 'openOutputDescription',
  OpenRelationsGraph: InputNodeAction.OpenRelationsGraph,
  OpenRelationsTable: InputNodeAction.OpenRelationsTable,
} as const;

export type OutputNodeAction =
  (typeof OutputNodeAction)[keyof typeof OutputNodeAction];

export const getEdgeStyle = (relationType: string) => ({
  markerEnd: {
    type: MarkerType.Arrow,
    width: 20,
    height: 20,
  },
  style:
    relationType === DataObjectRelationEdgeType.ALGO
      ? { strokeDasharray: '5,5' }
      : {},
});

// Clone nodes that create a loop with the root node (is both source & target)
export const cloneLoopingNodes = (
  nodes: RelationNodeType[],
  edges: RelationEdgeType[],
  rootNode: string | null,
): {
  updatedNodes: RelationNodeType[];
  updatedEdges: ElkExtendedEdge[];
} => {
  const finalNodes = [...nodes];
  let finalEdges = [...edges];

  nodes.forEach((node) => {
    if (!rootNode || node.id === rootNode) return;

    const hasToRoot = edges.some(
      (e) => e.source === node.id && e.target === rootNode,
    );
    const hasFromRoot = edges.some(
      (e) => e.source === rootNode && e.target === node.id,
    );
    const isLooped = hasToRoot && hasFromRoot;
    if (!isLooped) return;

    const cloneId = `${node.id}-clone`;
    finalNodes.push({ ...node, id: cloneId });

    finalEdges = finalEdges.map((e) => {
      if (e.source === node.id && e.target === rootNode) {
        return { ...e, source: cloneId };
      }
      if (e.target === node.id && e.source !== rootNode) {
        return { ...e, target: cloneId };
      }
      return e;
    });
  });

  return {
    updatedNodes: finalNodes,
    updatedEdges: finalEdges as unknown as ElkExtendedEdge[],
  };
};

export const getLayoutedElements = async (
  nodes: RelationNodeType[],
  edges: RelationEdgeType[],
  rootNode: string | null,
): Promise<{ nodes: RelationNodeType[]; edges: RelationEdgeType[] }> => {
  const { updatedNodes, updatedEdges } = cloneLoopingNodes(
    nodes,
    edges,
    rootNode,
  );

  const graph = {
    id: 'root',
    layoutOptions: elkOptions,
    children: updatedNodes.map((node) => ({
      ...node,
      targetPosition: Position.Left,
      sourcePosition: Position.Right,
      width: 177,
      height: node.data.activeVersion ? 80 : 56,
    })),
    edges: updatedEdges,
  };

  const layoutedGraph = await elk.layout(graph);

  return {
    nodes: (layoutedGraph.children ?? []).map((node) => ({
      ...node,
      position: {
        x: (node.x ?? 0) - (node.measured?.width ?? 0) / 2,
        y: (node.y ?? 0) - (node.measured?.height ?? 0) / 2,
      },
    })),
    edges: layoutedGraph.edges as unknown as RelationEdgeType[],
  };
};

export const getLatestValueDateFromVersions = (
  versions: DataObjectVersionType[],
) =>
  versions
    ? versions.reduce(
        (latest, version) => {
          if (!version.valueDate) return latest;

          if (!latest) {
            return version.valueDate;
          }

          const latestDate = new Date(latest);
          const currentDate = new Date(version.valueDate);

          return currentDate > latestDate ? version.valueDate : latest;
        },
        null as string | null,
      )
    : null;

export const getDescriptionVersionIdByValueDate = (
  valueDate: string | null,
  activePeriods: ActivePeriod[] | null,
) =>
  valueDate && Array.isArray(activePeriods) && activePeriods.length
    ? activePeriods
        .filter((p) => {
          const from = p.validFrom;
          const to = p.validTo as string | null;
          if (!from) return false;

          const gteFrom = valueDate >= from;
          const lteTo = to ? valueDate <= to : true;
          return gteFrom && lteTo;
        })
        .sort((a, b) => {
          if (!a.validFrom && !b.validFrom) return 0;
          if (!a.validFrom) return 1;
          if (!b.validFrom) return -1;
          return a.validFrom < b.validFrom ? 1 : -1;
        })[0]?.versionId
    : undefined;

export const getGroupNodeActiveVersion = (
  node: any,
): DataObjectVersionType | null => {
  const childNodes: NodeData[] =
    'childNodes' in node && Array.isArray(node.childNodes)
      ? (node.childNodes as unknown as NodeData[])
      : [];

  if (childNodes.length === 0) return null;

  const latestByChild = childNodes.map(
    (childNode) =>
      getLatestDataObjectVersion(
        childNode?.dataObjectVersions ?? [],
      ) as DataObjectVersionType | null,
  );

  if (latestByChild.some((v) => !v)) return null;

  const [first, ...rest] = latestByChild as DataObjectVersionType[];
  const allLatestVersionsHaveSameNumbers = rest.every(
    (v) =>
      v.versionNumber === first.versionNumber &&
      v.minorNumber === first.minorNumber,
  );
  const allLatestVersionsHaveSameStatus = rest.every(
    (v) => v.statusCode === first.statusCode,
  );

  let activeVersion = allLatestVersionsHaveSameStatus
    ? first
    : { ...first, statusCode: null };

  return allLatestVersionsHaveSameNumbers ? activeVersion : null;
};

export interface DataObjectColumnData {
  code: string;
  name: string;
}

export interface TimeParams {
  valueDate: string;
  calculationTime: string | null;
  timeLink: string | null;
}

export interface VersionInfo {
  statusCode: string;
  versionNumber: number | null;
  minorNumber: number | null;
  dataObjectType: string;
}

export interface ConflictRow {
  sequenceNumber: number;
  dataObjectA: DataObjectColumnData;
  timeParamsA: TimeParams[];
  versionA: VersionInfo[];
  dataObjectB: (DataObjectColumnData | null)[];
  timeParamsB: (TimeParams | null)[];
  versionB: (VersionInfo | null)[];
  dataObjectF: DataObjectColumnData | null;
  timeParamsF: {
    valueDate: string | null;
    calculationTime: string;
    timeLink: string | null;
  } | null;
  versionF: {
    statusCode: string | null;
    versionNumber: number | null;
    minorNumber: number | null;
  } | null;
}

const toDataObjectData = (
  source: CalculationSourceItem,
): DataObjectColumnData => ({
  code: source.data_object_description_code,
  name: source.data_object_description_name,
});

export interface BuildTimeLinkParams {
  dataObjectType: DataObjectType | string;
  groupId?: number | null;
  descriptionId?: number | null;
  versionId?: number | null;
  valueDate?: string | null;
  outputVersionId?: number | null;
}

export const buildTimeLink = ({
  dataObjectType,
  groupId,
  descriptionId,
  versionId,
  valueDate,
  outputVersionId,
}: BuildTimeLinkParams): string | null => {
  if (dataObjectType === DataObjectType.OUT) {
    if (groupId == null || versionId == null) return null;
    return (
      withParams(path.visualValidationCrossTable, {
        groupDescriptionRef: getGroupDescriptionRef(groupId, descriptionId),
        valueDate: valueDate ?? undefined,
        version: versionId,
      }) ?? null
    );
  }
  if (dataObjectType === DataObjectType.IN) {
    if (descriptionId == null || versionId == null) return null;
    return (
      withParams(path.inputDataView, {
        id: String(descriptionId),
        date: valueDate ?? undefined,
        type: 'description',
        inputDataVersionId: versionId,
        outputDataVersionId: outputVersionId ?? undefined,
      }) ?? null
    );
  }
  return null;
};

const toTimeParams = (
  source: CalculationSourceItem,
  outputVersionId?: number | null,
): TimeParams => ({
  valueDate: source.value_date,
  calculationTime: source.calculation_time,
  timeLink: buildTimeLink({
    dataObjectType: source.data_object_type,
    groupId: source.data_object_group_id,
    descriptionId: source.data_object_description_id,
    versionId: source.data_object_version_id,
    valueDate: source.value_date,
    outputVersionId,
  }),
});

const toVersionInfo = (source: CalculationSourceItem): VersionInfo => ({
  statusCode: source.status_code,
  versionNumber: source.version_number,
  minorNumber: source.minor_number,
  dataObjectType: source.data_object_type,
});

const getReachableDataObject = (
  dagRelations: Record<string, number[]>,
  from: number,
): Set<number> => {
  const visited = new Set<number>();
  const queue = [from];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    if (visited.has(cur)) continue;
    visited.add(cur);
    for (const next of dagRelations[String(cur)] ?? []) queue.push(next);
  }
  visited.delete(from);
  return visited;
};

const buildParentMap = (
  dagRelations: Record<string, number[]>,
): Record<string, number[]> => {
  const parentMap: Record<string, number[]> = {};
  for (const [from, tos] of Object.entries(dagRelations)) {
    for (const to of tos) {
      const key = String(to);
      if (!parentMap[key]) parentMap[key] = [];
      parentMap[key].push(Number(from));
    }
  }
  return parentMap;
};

export const buildConflictRows = (
  data: DataObjectRelationsDataConflictsItem[],
): ConflictRow[] => {
  const rows: ConflictRow[] = [];

  for (const fVersion of data) {
    const { sources, dag, conflicts } = fVersion.calculationSources;

    const sourcesById = new Map<number, CalculationSourceItem>(
      sources.map((s) => [s.data_object_version_id, s]),
    );

    const fSource = sourcesById.get(fVersion.id) ?? null;
    const parentMap = buildParentMap(dag);
    const canReachF = getReachableDataObject(parentMap, fVersion.id);

    let conflictNumber = 0;
    for (const conflictVersionIds of Object.values(conflicts)) {
      const subRows: Array<{
        a: CalculationSourceItem;
        aOutputVersionId: number | null;
        b: CalculationSourceItem | null;
      }> = [];

      for (const aVersionId of conflictVersionIds) {
        const aSource = sourcesById.get(aVersionId);
        if (!aSource) continue;

        const aOutputVersionId =
          (dag[String(aVersionId)] ?? []).find(
            (depId) =>
              sourcesById.get(depId)?.data_object_type === DataObjectType.OUT,
          ) ?? null;

        const reachableFromA = getReachableDataObject(dag, aVersionId);
        const dataObjectBIds = [...reachableFromA].filter(
          (id) =>
            id !== fVersion.id &&
            !conflictVersionIds.includes(id) &&
            canReachF.has(id),
        );

        if (dataObjectBIds.length > 0) {
          for (const bId of dataObjectBIds) {
            subRows.push({
              a: aSource,
              aOutputVersionId,
              b: sourcesById.get(bId) ?? null,
            });
          }
        } else {
          subRows.push({ a: aSource, aOutputVersionId, b: null });
        }
      }

      if (subRows.length > 0) {
        rows.push({
          sequenceNumber: conflictNumber + 1,
          dataObjectA: toDataObjectData(subRows[0].a),
          timeParamsA: subRows.map((s) =>
            toTimeParams(s.a, s.aOutputVersionId),
          ),
          versionA: subRows.map((s) => toVersionInfo(s.a)),
          dataObjectB: subRows.map((s) => (s.b ? toDataObjectData(s.b) : null)),
          timeParamsB: subRows.map((s) => (s.b ? toTimeParams(s.b) : null)),
          versionB: subRows.map((s) => (s.b ? toVersionInfo(s.b) : null)),
          dataObjectF: fSource ? toDataObjectData(fSource) : null,
          timeParamsF: {
            valueDate: fVersion.valueDate ?? null,
            calculationTime: fVersion.calculationTime,
            timeLink: fSource
              ? buildTimeLink({
                  dataObjectType: DataObjectType.OUT,
                  groupId: fSource.data_object_group_id,
                  descriptionId: fSource.data_object_description_id,
                  versionId: fVersion.id,
                  valueDate: fVersion.valueDate ?? null,
                })
              : null,
          },
          versionF: {
            statusCode: fVersion.statusCode ?? null,
            versionNumber: fVersion.versionNumber ?? null,
            minorNumber: fVersion.minorNumber ?? null,
          },
        });
      }

      conflictNumber++;
    }
  }

  return rows;
};
